(function (global) {
  'use strict';

  const schemaVersion = 'voler-finder-customer-event-v1';
  const eventRules = Object.freeze({
    voler_finder_start: ['question_id', 'answer_id', 'step_number'],
    voler_finder_answer: ['question_id', 'answer_id', 'step_number'],
    voler_finder_complete: ['result_type', 'exact_match_count', 'alternative_count', 'recommendation_count'],
    voler_finder_no_match: ['result_type', 'exact_match_count', 'alternative_count', 'recommendation_count'],
    voler_finder_result_click: ['result_type', 'recommendation_key', 'recommendation_position', 'destination_type']
  });
  const commonFields = ['finder_category', 'finder_version', 'page_path', 'elapsed_time_bucket'];
  const allowedEvents = new Set(Object.keys(eventRules));
  const sessionStorageKey = 'voler_finder_measurement_session_v2';
  const attributionStorageKey = 'voler_finder_attribution_v2';
  const retiredStorageKeys = ['voler_finder_session_id', 'voler_finder_attribution_v1'];
  const sessionTimeoutMs = 30 * 60 * 1000;
  const forbiddenField = /(^|_)(email|name|customer|order|transaction|phone|address|full_url|free_text)($|_)/i;
  const slugValue = /^[a-z0-9][a-z0-9_-]{0,79}$/;
  const versionValue = /^(bottoms|jersey)-finder-v[1-9][0-9]*$/;
  const pagePathValue = /^\/[A-Za-z0-9/_.-]*$/;
  const resultTypes = new Set(['exact_match', 'closest_alternative', 'no_match']);
  const destinationTypes = new Set(['construction_view', 'pdp']);
  const elapsedBuckets = new Set(['not_started', 'under_15s', '15_to_29s', '30_to_59s', '1_to_2m', '2_to_5m', 'over_5m']);
  const versionRules = Object.freeze({
    'bottoms-finder-v2': Object.freeze({
      category: 'bottoms',
      pagePath: '/pages/bottoms-finder',
      answers: Object.freeze({
        rider: ['men', 'women'], conditions: ['warm', 'thermal'], duration: ['short', 'medium', 'long'],
        support: ['easy', 'balanced', 'firm', 'unsure'], construction: ['bib', 'short', 'either'],
        storage: ['clean', 'cargo', 'either'], padding: ['padded', 'unpadded', 'either']
      }),
      recommendations: new Set(['peloton-men-bib', 'peloton-men-short', 'peloton-women-bib', 'peloton-women-short', 'escape-men-bib', 'escape-men-short', 'escape-women-bib', 'escape-women-short', 'shift-escape-men-bib', 'shift-escape-men-cargo', 'shift-pace-men-bib', 'escape-knicker-men', 'black-label-men-bib', 'black-label-men-short', 'black-label-men-cargo', 'black-label-women-bib', 'black-label-women-short', 'black-label-women-cargo', 'super-men-bib', 'super-men-cargo', 'super-women-bib', 'super-women-cargo', 'thermal-cargo-men', 'thermal-cargo-women', 'thermal-layering-men', 'thermal-layering-women', 'thermal-knicker-men', 'jet-layering-men'])
    }),
    'jersey-finder-v1': Object.freeze({
      category: 'jerseys',
      pagePath: '/pages/jersey-finder',
      answers: Object.freeze({
        rider: ['men', 'women', 'youth'], purpose: ['road', 'gravel', 'mtb', 'triathlon', 'casual', 'unsure'],
        sleeve: ['short', 'long', 'sleeveless', 'hooded', 'unsure'], conditions: ['hot', 'warm', 'cool', 'cold', 'unsure'],
        fit: ['aero', 'race', 'club', 'casual', 'unsure'], material: ['synthetic', 'merino', 'unsure'],
        comfort: ['breathability', 'balanced', 'warmth', 'unsure'], sun: ['high', 'some', 'no', 'unsure'],
        zipper: ['full', 'zipperless', 'unsure']
      }),
      recommendations: new Set(['velocity-air-ss', 'velocity-air-ls', 'pro-race-ss', 'pro-air-zipperless-ss', 'peloton-race-ss', 'peloton-club-ss', 'classic-ss', 'merino-lightweight-zippered-ss', 'merino-lightweight-zipperless-ss', 'merino-midweight-ls', 'merino-lightweight-ss-tee', 'merino-lightweight-ls-tee', 'merino-midweight-hoodie', 'jet-men-ss', 'jet-escape-women-ss', 'jet-lightweight-ls', 'jet-thermal-ls', 'peloton-lightweight-ls', 'peloton-thermal-ls', 'tempe-ls', 'endurance-air-ss-tee', 'endurance-air-ls-tee', 'endurance-air-sun-hoodie', 'endurance-air-tank', 'crop-tee', 'youth-endurance-air-tee', 'youth-race-ss', 'youth-club-ss', 'youth-general-ss', 'xc-ss', 'trail-ss', 'performance-tri-ss', 'performance-tri-sleeveless'])
    })
  });

  function stableValue(value) {
    if (Array.isArray(value)) return value.map(stableValue);
    if (!value || typeof value !== 'object') return value;
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, stableValue(value[key])]));
  }

  function stableSignature(value) {
    return JSON.stringify(stableValue(value));
  }

  function boundedValue(value, depth = 0) {
    if (value === null || typeof value === 'boolean') return true;
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value === 'string') return value.length <= 120 && !/^https?:\/\//i.test(value);
    if (depth >= 3) return false;
    if (Array.isArray(value)) return value.length <= 40 && value.every((entry) => boundedValue(entry, depth + 1));
    if (!value || typeof value !== 'object') return false;
    return Object.entries(value).every(([key, entry]) => !forbiddenField.test(key) && boundedValue(entry, depth + 1));
  }

  function analyticsConsentAllowed() {
    try {
      return global.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (_) {
      return false;
    }
  }

  function customerEventPublisher() {
    return typeof global.Shopify?.analytics?.publish === 'function'
      ? global.Shopify.analytics.publish.bind(global.Shopify.analytics)
      : null;
  }

  function consentInterfaceState(consentAllowed) {
    try {
      if (typeof consentAllowed === 'function') {
        const allowed = consentAllowed();
        if (allowed === true) return { consent_interface: 'present', analytics_processing_allowed: true };
        if (allowed === false) return { consent_interface: 'present', analytics_processing_allowed: false };
        return { consent_interface: 'present', analytics_processing_allowed: null };
      }
      const privacy = global.Shopify?.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') {
        return { consent_interface: privacy ? 'present_incomplete' : 'absent', analytics_processing_allowed: null };
      }
      const allowed = privacy.analyticsProcessingAllowed();
      if (allowed === true) return { consent_interface: 'present', analytics_processing_allowed: true };
      if (allowed === false) return { consent_interface: 'present', analytics_processing_allowed: false };
      return { consent_interface: 'present', analytics_processing_allowed: null };
    } catch (_) {
      return { consent_interface: 'error', analytics_processing_allowed: null };
    }
  }

  function publisherState(publish) {
    try {
      if (typeof publish === 'function') return 'present';
      return customerEventPublisher() ? 'present' : 'absent';
    } catch (_) {
      return 'error';
    }
  }

  function probePagePath(finderVersion, fallbackPath) {
    return versionRules[finderVersion]?.pagePath || fallbackPath || '/';
  }

  function observeConsentPublisher(options) {
    const root = options?.root;
    const enabled = root?.dataset?.finderAnalyticsEnabled === 'true';
    const consent = consentInterfaceState(options?.consentAllowed);
    const publisher = publisherState(options?.publish);
    const finderVersion = options?.finderVersion || null;
    const finderCategory = options?.finderCategory || null;
    const pagePath = probePagePath(finderVersion, global.location?.pathname || '/');
    const wouldPublish = enabled === true && consent.analytics_processing_allowed === true && publisher === 'present';
    return Object.freeze({
      schema_version: 'voler-finder-consent-publisher-probe-v1',
      analytics_enabled: enabled === true,
      consent_interface: consent.consent_interface,
      analytics_processing_allowed: consent.analytics_processing_allowed,
      publisher,
      would_publish: wouldPublish,
      publish_attempted: false,
      publish_result: 'not_attempted',
      event_name: null,
      finder_category: finderCategory,
      finder_version: finderVersion,
      page_path: pagePath
    });
  }

  function probeConsentPublisher(options) {
    const observation = { ...observeConsentPublisher(options) };
    if (options?.emit !== true) return Object.freeze(observation);
    if (observation.analytics_enabled !== true) {
      return Object.freeze({ ...observation, publish_result: 'blocked_disabled' });
    }
    if (observation.analytics_processing_allowed !== true) {
      return Object.freeze({ ...observation, publish_result: 'blocked_consent' });
    }
    if (observation.publisher !== 'present') {
      return Object.freeze({ ...observation, publish_result: 'blocked_publisher' });
    }
    const eventName = 'voler_finder_start';
    const customData = {
      finder_version: observation.finder_version,
      finder_category: observation.finder_category,
      page_path: observation.page_path,
      elapsed_time_bucket: 'not_started',
      question_id: 'rider',
      answer_id: 'men',
      step_number: 1
    };
    if (!validCustomData(eventName, customData)) {
      return Object.freeze({ ...observation, publish_result: 'invalid' });
    }
    const publish = typeof options?.publish === 'function' ? options.publish : customerEventPublisher();
    if (typeof publish !== 'function') {
      return Object.freeze({ ...observation, publish_result: 'blocked_publisher' });
    }
    try {
      publish(eventName, customData);
      return Object.freeze({
        ...observation,
        would_publish: true,
        publish_attempted: true,
        publish_result: 'published',
        event_name: eventName
      });
    } catch (_) {
      return Object.freeze({
        ...observation,
        publish_attempted: true,
        publish_result: 'threw'
      });
    }
  }

  function requestConsentFeature(done) {
    const loadFeatures = global.Shopify?.loadFeatures;
    if (typeof loadFeatures !== 'function') {
      done(new Error('loadFeatures unavailable'));
      return;
    }
    try {
      loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => done(error || null));
    } catch (error) {
      done(error);
    }
  }

  function randomId() {
    return global.crypto?.randomUUID?.() || `vf-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function elapsedTimeBucket(elapsedMs) {
    if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return null;
    if (elapsedMs < 15000) return 'under_15s';
    if (elapsedMs < 30000) return '15_to_29s';
    if (elapsedMs < 60000) return '30_to_59s';
    if (elapsedMs < 180000) return '1_to_2m';
    if (elapsedMs < 360000) return '2_to_5m';
    return 'over_5m';
  }

  function validCustomData(eventName, payload) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
    const required = eventRules[eventName];
    if (!required) return false;
    const allowed = new Set([...commonFields, ...required]);
    if (required.some((key) => payload[key] === undefined || payload[key] === null || payload[key] === '')) return false;
    for (const [key, value] of Object.entries(payload)) {
      if (!allowed.has(key) || forbiddenField.test(key)) return false;
      if (Array.isArray(value) || (value && typeof value === 'object')) return false;
    }
    if (!versionValue.test(payload.finder_version || '')) return false;
    const versionRule = versionRules[payload.finder_version];
    if (!versionRule || payload.finder_category !== versionRule.category || payload.page_path !== versionRule.pagePath) return false;
    if (!pagePathValue.test(payload.page_path || '') || !elapsedBuckets.has(payload.elapsed_time_bucket)) return false;
    for (const key of ['question_id', 'answer_id', 'recommendation_key']) {
      if (payload[key] !== undefined && !slugValue.test(payload[key])) return false;
    }
    if (payload.question_id !== undefined) {
      const allowedAnswers = versionRule.answers[payload.question_id];
      if (!allowedAnswers || (payload.answer_id !== undefined && !allowedAnswers.includes(payload.answer_id))) return false;
    }
    if (payload.recommendation_key !== undefined && !versionRule.recommendations.has(payload.recommendation_key)) return false;
    for (const key of ['step_number', 'exact_match_count', 'alternative_count', 'recommendation_count', 'recommendation_position']) {
      if (payload[key] !== undefined && (!Number.isInteger(payload[key]) || payload[key] < 0 || payload[key] > 100)) return false;
    }
    for (const key of ['step_number', 'recommendation_position']) {
      if (payload[key] !== undefined && payload[key] < 1) return false;
    }
    if (payload.result_type !== undefined && !resultTypes.has(payload.result_type)) return false;
    if (payload.destination_type !== undefined && !destinationTypes.has(payload.destination_type)) return false;
    if (eventName === 'voler_finder_result_click' && payload.result_type === 'no_match') return false;
    if (['voler_finder_complete', 'voler_finder_no_match'].includes(eventName)) {
      const total = payload.exact_match_count + payload.alternative_count;
      if (payload.recommendation_count !== total) return false;
      if (payload.result_type === 'exact_match' && (payload.exact_match_count < 1 || payload.alternative_count !== 0)) return false;
      if (payload.result_type === 'closest_alternative' && (payload.exact_match_count !== 0 || payload.alternative_count < 1)) return false;
      if (payload.result_type === 'no_match' && total !== 0) return false;
      if (eventName === 'voler_finder_no_match' && payload.result_type === 'exact_match') return false;
    }
    return true;
  }

  function create(options) {
    const root = options?.root;
    const finderVersion = options?.finderVersion;
    const finderCategory = options?.finderCategory;
    if (!root || !finderVersion || !['bottoms', 'jerseys'].includes(finderCategory)) return null;

    const storage = options.storage || global.sessionStorage;
    const now = options.now || (() => Date.now());
    const createId = options.createId || randomId;
    const consentAllowed = options.consentAllowed || analyticsConsentAllowed;
    const providedPublish = Object.prototype.hasOwnProperty.call(options, 'publish')
      ? options.publish
      : undefined;
    function resolvePublisher() {
      if (typeof providedPublish === 'function') return providedPublish;
      if (providedPublish === null) return null;
      return customerEventPublisher();
    }
    const enabled = root.dataset.finderAnalyticsEnabled === 'true';
    const completedSignatures = new Set();
    let engagedAt = 0;
    let startSent = false;
    let sessionId = null;

    function removeStoredMeasurement() {
      try {
        storage.removeItem(sessionStorageKey);
        storage.removeItem(attributionStorageKey);
        retiredStorageKeys.forEach((key) => storage.removeItem(key));
      } catch (_) {
        // Finder behavior must not depend on storage availability.
      }
      sessionId = null;
      engagedAt = 0;
      startSent = false;
      completedSignatures.clear();
    }

    function canCollect() {
      const publish = resolvePublisher();
      const allowed = enabled && consentAllowed() === true && typeof publish === 'function';
      if (!allowed) removeStoredMeasurement();
      return allowed;
    }

    function readStored(key) {
      try {
        const value = JSON.parse(storage.getItem(key) || 'null');
        if (!value || value.expires_at <= now()) {
          storage.removeItem(key);
          return null;
        }
        return value;
      } catch (_) {
        try { storage.removeItem(key); } catch (_) { /* no-op */ }
        return null;
      }
    }

    function writeStored(key, value) {
      try {
        storage.setItem(key, JSON.stringify(value));
        return true;
      } catch (_) {
        return false;
      }
    }

    function ensureSession() {
      const timestamp = now();
      const stored = readStored(sessionStorageKey);
      if (stored?.finder_session_id) sessionId = stored.finder_session_id;
      if (!sessionId) sessionId = createId();
      writeStored(sessionStorageKey, { finder_session_id: sessionId, last_activity_at: timestamp, expires_at: timestamp + sessionTimeoutMs });
      return sessionId;
    }

    function commonCustomData() {
      return {
        finder_version: finderVersion,
        finder_category: finderCategory,
        page_path: global.location?.pathname || '/',
        elapsed_time_bucket: elapsedTimeBucket(engagedAt ? now() - engagedAt : 0)
      };
    }

    function emit(eventName, fields) {
      if (!canCollect() || !allowedEvents.has(eventName)) return null;
      const publish = resolvePublisher();
      const customData = { ...commonCustomData(), ...fields };
      if (!validCustomData(eventName, customData) || typeof publish !== 'function') return null;
      try { publish(eventName, customData); } catch (_) { return null; }
      return { event: eventName, customData };
    }

    function saveAttribution(answers, fields) {
      if (!canCollect()) return false;
      const timestamp = now();
      const record = {
        schema_version: 2,
        finder_version: finderVersion,
        finder_category: finderCategory,
        finder_session_id: ensureSession(),
        answers: stableValue(answers || {}),
        ...fields,
        last_activity_at: timestamp,
        expires_at: timestamp + sessionTimeoutMs
      };
      if (!boundedValue(record)) return false;
      return writeStored(attributionStorageKey, record);
    }

    function answer({ stepNumber, questionId, answerId, answers }) {
      if (!canCollect() || !boundedValue(answers || {})) return [];
      const emitted = [];
      if (!startSent) {
        engagedAt = now();
        const start = emit('voler_finder_start', { question_id: questionId, answer_id: answerId, step_number: stepNumber });
        if (start) {
          startSent = true;
          ensureSession();
          emitted.push(start);
        }
      }
      const selected = emit('voler_finder_answer', { step_number: stepNumber, question_id: questionId, answer_id: answerId });
      if (selected) emitted.push(selected);
      return emitted;
    }

    function complete({ answers, resultType, exactMatchCount, alternativeCount }) {
      if (!canCollect() || !boundedValue(answers || {})) return [];
      const recommendationCount = exactMatchCount + alternativeCount;
      const signature = stableSignature({ finder_version: finderVersion, answers, result_type: resultType, exact_match_count: exactMatchCount, alternative_count: alternativeCount, recommendation_count: recommendationCount });
      if (completedSignatures.has(signature)) return [];
      const fields = { result_type: resultType, exact_match_count: exactMatchCount, alternative_count: alternativeCount, recommendation_count: recommendationCount };
      const completion = emit('voler_finder_complete', fields);
      if (!completion) return [];
      completedSignatures.add(signature);
      const emitted = [completion];
      if (exactMatchCount === 0) {
        const noMatch = emit('voler_finder_no_match', fields);
        if (noMatch) emitted.push(noMatch);
      }
      saveAttribution(answers, fields);
      return emitted;
    }

    function resultClick({ answers, recommendationKey, selectedProductHandle, resultType, recommendationPosition, destinationType }) {
      if (!boundedValue(answers || {})) return null;
      const fields = { result_type: resultType, recommendation_key: recommendationKey, recommendation_position: recommendationPosition, destination_type: destinationType };
      const click = emit('voler_finder_result_click', fields);
      if (!click) return null;
      saveAttribution(answers, { result_type: resultType, selected_product_key: recommendationKey, selected_product_handle: selectedProductHandle });
      return click;
    }

    function attributionContext() {
      if (!canCollect()) return null;
      const record = readStored(attributionStorageKey);
      if (!record) return null;
      return {
        finder_assisted: true,
        finder_version: record.finder_version,
        finder_category: record.finder_category,
        finder_session_id: record.finder_session_id,
        selected_product_key: record.selected_product_key || null,
        selected_product_handle: record.selected_product_handle || null
      };
    }

    function handleConsentChange() {
      if (!enabled || consentAllowed() !== true || typeof resolvePublisher() !== 'function') removeStoredMeasurement();
    }

    if (!enabled || typeof resolvePublisher() !== 'function') removeStoredMeasurement();
    global.document?.addEventListener?.('visitorConsentCollected', handleConsentChange);

    function probe(options) {
      return probeConsentPublisher({
        root,
        finderVersion,
        finderCategory,
        consentAllowed,
        publish: resolvePublisher(),
        emit: options?.emit === true
      });
    }

    return Object.freeze({
      answer, complete, resultClick, attributionContext, clear: removeStoredMeasurement,
      observe: () => observeConsentPublisher({
        root,
        finderVersion,
        finderCategory,
        consentAllowed,
        publish: resolvePublisher()
      }),
      probe,
      destroy() { global.document?.removeEventListener?.('visitorConsentCollected', handleConsentChange); }
    });
  }

  global.VolerFinderMeasurement = Object.freeze({
    create,
    observeConsentPublisher,
    probeConsentPublisher,
    requestConsentFeature,
    schemaVersion,
    probeSchemaVersion: 'voler-finder-consent-publisher-probe-v1',
    eventNames: Object.freeze([...allowedEvents]),
    validateCustomData: validCustomData,
    sessionStorageKey,
    attributionStorageKey,
    sessionTimeoutMs
  });
})(window);
